import React, { useEffect, useState, useRef } from 'react';
/**
 * MaternalHeartRateWave
 *
 * A self-contained, infinitely-running ECG-style waveform that simulates a
 * resting maternal heart rate between 64–80 bpm. This is TEST/LIVE-PREVIEW data
 * only — it does not hit any API. It is designed to be swapped out for a real
 * data source (e.g. the device WebSocket) later without changing the layout.
 *
 * The trace is drawn on a canvas that scrolls right-to-left. Each heartbeat
 * injects a synthetic PQRST complex, with beat spacing derived from the current
 * (slowly drifting) bpm so the rhythm visibly tracks the displayed number.
 */
const MIN_BPM = 64;
const MAX_BPM = 80;
// How many vertical samples make up one screen-width of trace.
const SAMPLES = 240;
// Horizontal scroll speed in samples per second.
const SAMPLES_PER_SECOND = 90;
// Synthetic ECG: sum of gaussians for the P, Q, R, S, T deflections.
// `phase` is the position within a single beat, in [0, 1).
function ecgSample(phase: number): number {
  const g = (center: number, amp: number, width: number) => amp * Math.exp(-((phase - center) ** 2) / (2 * width * width));
  const p = g(0.2, 0.08, 0.025);
  const q = g(0.36, -0.12, 0.012);
  const r = g(0.4, 1.0, 0.011);
  const s = g(0.45, -0.22, 0.013);
  const t = g(0.68, 0.22, 0.04);
  return p + q + r + s + t;
}
export function MaternalHeartRateWave() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [displayBpm, setDisplayBpm] = useState(72);
  // Mutable animation state kept in refs so the rAF loop stays stable.
  const samplesRef = useRef<number[]>(new Array(SAMPLES).fill(0));
  const bpmRef = useRef(72);
  const targetBpmRef = useRef(72);
  const beatPhaseRef = useRef(0);
  const lastTimeRef = useRef<number | null>(null);
  const lastDriftRef = useRef(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let rafId = 0;
    let isMounted = true;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    const draw = (time: number) => {
      if (!isMounted) return;
      if (lastTimeRef.current == null) lastTimeRef.current = time;
      const dt = Math.min((time - lastTimeRef.current) / 1000, 0.05);
      lastTimeRef.current = time;
      // Slowly drift the target bpm every few seconds, then ease toward it.
      if (time - lastDriftRef.current > 3500) {
        targetBpmRef.current = MIN_BPM + Math.random() * (MAX_BPM - MIN_BPM);
        lastDriftRef.current = time;
      }
      bpmRef.current += (targetBpmRef.current - bpmRef.current) * dt * 0.6;
      // Advance the beat phase. One full beat == 60 / bpm seconds.
      const beatsPerSecond = bpmRef.current / 60;
      beatPhaseRef.current += dt * beatsPerSecond;
      if (beatPhaseRef.current >= 1) beatPhaseRef.current -= 1;
      // Push new samples for the time elapsed (scrolling trace).
      const newCount = Math.max(1, Math.round(dt * SAMPLES_PER_SECOND));
      const buf = samplesRef.current;
      for (let i = 0; i < newCount; i++) {
        const phase = (beatPhaseRef.current + i / newCount * dt * beatsPerSecond) % 1;
        buf.push(ecgSample(phase));
      }
      while (buf.length > SAMPLES) buf.shift();
      // Render
      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      ctx.clearRect(0, 0, w, h);
      // Subtle baseline grid
      ctx.strokeStyle = 'rgba(0, 212, 212, 0.08)';
      ctx.lineWidth = 1;
      for (let gx = 0; gx <= w; gx += 28) {
        ctx.beginPath();
        ctx.moveTo(gx, 0);
        ctx.lineTo(gx, h);
        ctx.stroke();
      }
      const mid = h * 0.58;
      const amp = h * 0.34;
      const grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0, '#00D4D4');
      grad.addColorStop(1, '#6B21E8');
      ctx.strokeStyle = grad;
      ctx.lineWidth = 2.5;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.shadowColor = 'rgba(107, 33, 232, 0.25)';
      ctx.shadowBlur = 6;
      ctx.beginPath();
      const n = buf.length;
      for (let i = 0; i < n; i++) {
        const x = i / (n - 1) * w;
        const y = mid - buf[i] * amp;
        if (i === 0) ctx.moveTo(x, y);else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
      // Leading dot
      if (n > 0) {
        const lastY = mid - buf[n - 1] * amp;
        ctx.fillStyle = '#6B21E8';
        ctx.beginPath();
        ctx.arc(w - 1, lastY, 3, 0, Math.PI * 2);
        ctx.fill();
      }
      rafId = requestAnimationFrame(draw);
    };
    rafId = requestAnimationFrame(draw);
    // Keep the displayed number in sync at a calm cadence (not every frame).
    const bpmInterval = setInterval(() => {
      if (isMounted) setDisplayBpm(Math.round(bpmRef.current));
    }, 800);
    return () => {
      isMounted = false;
      cancelAnimationFrame(rafId);
      clearInterval(bpmInterval);
      window.removeEventListener('resize', resize);
    };
  }, []);
  return <div className="rounded-[16px] bg-secondary/40 border border-border/60 p-4 mb-5" data-id="element-1601" data-mp="cktukczk7w80">
      <div className="flex items-center justify-between mb-3" data-id="element-1602" data-mp="cmwrlgz4qpm8">
        <div className="flex items-center gap-2" data-id="element-1603" data-mp="199rra0qve47t">
          <span className="relative flex h-2 w-2" data-id="element-1604" data-mp="14bzy73ehoc21">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" data-id="element-1605" data-mp="14db5veb223yf" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" data-id="element-1606" data-mp="zjgnce76rv0x" />
          </span>
          <span className="text-[12px] font-semibold uppercase tracking-wider text-text-secondary" data-id="element-1607" data-mp="vjfxzyijtzf3">
            Live
          </span>
        </div>
        <div className="flex items-baseline" data-id="element-1608" data-mp="1nacagy0p621u">
          <span className="text-[28px] font-bold text-text-primary leading-none tabular-nums" data-id="element-1609" data-mp="1n9kpeh4q7mpt">
            {displayBpm}
          </span>
          <span className="text-[12px] text-text-secondary font-medium ml-1.5 tracking-wider uppercase" data-id="element-1610" data-mp="3mhsyau3suait">
            bpm
          </span>
        </div>
      </div>
      <canvas ref={canvasRef} className="w-full h-[88px] block" role="img" aria-label={`Live maternal heart rate waveform, currently ${displayBpm} beats per minute`} data-id="element-1611" data-mp="37smrol2mti0w" />
    </div>;
}